'use client'

import { useEffect, useState } from 'react'

/** Fixed navbar element ids across public / auth / dashboard / loading shells. */
const NAVBAR_IDS = [
  'navbar-links',
  'navbar-auth',
  'navbar-dashboard',
  'navbar-loading',
  'navbar-auth-loading',
] as const

const HYDRATED_NAVBAR_IDS = new Set(['navbar-links', 'navbar-auth', 'navbar-dashboard'])

/** The fixed navbar element currently mounted, across every shell variant. */
export function findNavbar(): HTMLElement | null {
  for (const id of NAVBAR_IDS) {
    const el = document.getElementById(id)
    if (el) return el
  }
  return null
}

/**
 * Live offsetHeight of the fixed navbar (ignores translateY hide transform).
 * Used so sticky peers sit flush under the real bar instead of `.page-top` padding.
 */
export function useNavbarHeight(): number | null {
  const [height, setHeight] = useState<number | null>(null)

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null
    let mutationObserver: MutationObserver | null = null
    let current: HTMLElement | null = null

    function attach(el: HTMLElement) {
      if (current === el) return
      resizeObserver?.disconnect()
      current = el
      const update = () => setHeight(el.offsetHeight)
      update()
      resizeObserver = new ResizeObserver(update)
      resizeObserver.observe(el)
    }

    function scan() {
      const el = findNavbar()
      if (!el) return
      attach(el)
      // Stop watching the tree once the hydrated navbar is mounted.
      if (HYDRATED_NAVBAR_IDS.has(el.id) && mutationObserver) {
        mutationObserver.disconnect()
        mutationObserver = null
      }
    }

    scan()
    mutationObserver = new MutationObserver(scan)
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    return () => {
      mutationObserver?.disconnect()
      resizeObserver?.disconnect()
    }
  }, [])

  return height
}
